/**
 * The action bar for selected queue rows: Mark done, Not for us, Waiting for customer
 * and Take, for many cases in one go.
 *
 * Closing asks first ("Mark 37 cases as done? You can reopen each one."). Each case is
 * its own write with the rules of the case page (version check, the first status takes
 * the case, reopening a colleague's case needs a manager), so the call as a whole is not
 * all-or-nothing: the bar says how many changed and which a colleague changed in the
 * meantime, and "Select the 2" picks those again. Every write reaches the app database
 * only; nothing is sent.
 */

import { ActionBar, AlertDialog, Button, Typography } from "@norican/react";
import { useState } from "react";

import { type BulkRequest, type BulkResult, useBulkWork } from "@/lib/api";
import { logger } from "@/lib/logger";
import { type QuoteCaseSummary } from "@/lib/mock";
import { type BulkAction, bulkConfirmText, bulkResultText, changedIds } from "@/lib/sales-flow";
import { track } from "@/lib/usage";

const REQUESTS: Record<Exclude<BulkAction, "open" | "release">, BulkRequest> = {
  done: { kind: "status", status: "done" },
  not_for_us: { kind: "status", status: "not_for_us" },
  waiting_customer: { kind: "status", status: "waiting_customer" },
  take: { kind: "assign", action: "take" },
};

export interface BulkBarProps {
  /** The selected rows as the queue shows them. */
  rows: QuoteCaseSummary[];
  /** Selects exactly these case ids (e.g. the ones a colleague changed). */
  onSelect: (ids: string[]) => void;
  onClear: () => void;
}

export function BulkBar({ rows, onSelect, onClear }: BulkBarProps) {
  const bulk = useBulkWork();
  const [asking, setAsking] = useState<"done" | "not_for_us" | null>(null);
  const [outcome, setOutcome] = useState<{ action: BulkAction; result: BulkResult } | null>(null);
  const count = rows.length;

  const run = (action: Exclude<BulkAction, "open" | "release">) => {
    const request = REQUESTS[action];
    logger.debug("[queue] bulk", { action, cases: count });
    track("queue.bulk", { action, count });
    setOutcome(null);
    bulk.mutate(
      {
        rows: rows.map((row) => ({
          id: row.id,
          workStatus: row.workStatus ?? "open",
          assignee: row.assignee,
        })),
        request,
      },
      {
        onSuccess: (result) => {
          setOutcome({ action, result });
          // The changed rows leave the list on the next read; "Select the N" picks the rest.
          onSelect([]);
        },
        onError: (error) => logger.warn("[queue] bulk failed", error),
      },
    );
  };

  const again = changedIds(outcome?.result);
  const message = bulk.isError ? (
    <Typography as="span" variant="caption" className="text-danger-fg" role="alert">
      Nothing changed. The case store is not reachable now. Try again in a moment.
    </Typography>
  ) : outcome ? (
    <span className="flex flex-wrap items-center gap-2" role="status">
      <Typography as="span" variant="caption" className="text-fg">
        {bulkResultText(outcome.result, outcome.action)}.
      </Typography>
      {again.length > 0 && (
        <Button
          size="sm"
          variant="ghost"
          tone="accent"
          onClick={() => {
            onSelect(again);
            setOutcome(null);
          }}
        >
          Select the {again.length}
        </Button>
      )}
      {count === 0 && (
        <Button size="sm" variant="ghost" tone="neutral" onClick={() => setOutcome(null)}>
          Close
        </Button>
      )}
    </span>
  ) : null;

  if (count === 0 && !outcome && !bulk.isError) {
    return null;
  }

  return (
    <>
      <ActionBar
        sticky="bottom"
        selectedCount={count}
        message={message}
        aria-label="Change the selected cases"
        className="bg-surface"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            tone="accent"
            disabled={count === 0}
            loading={bulk.isPending && bulk.variables?.request === REQUESTS.done}
            onClick={() => setAsking("done")}
          >
            Mark done
          </Button>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={count === 0 || bulk.isPending}
            onClick={() => setAsking("not_for_us")}
          >
            Not for us
          </Button>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={count === 0 || bulk.isPending}
            onClick={() => run("waiting_customer")}
          >
            Waiting for customer
          </Button>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={count === 0 || bulk.isPending}
            onClick={() => run("take")}
          >
            Take
          </Button>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            disabled={bulk.isPending}
            onClick={() => {
              setOutcome(null);
              bulk.reset();
              onClear();
            }}
          >
            Clear selection
          </Button>
        </div>
      </ActionBar>

      <AlertDialog.Root open={asking !== null} onOpenChange={(open) => !open && setAsking(null)}>
        <AlertDialog.Portal>
          <AlertDialog.Backdrop />
          <AlertDialog.Popup>
            <AlertDialog.Title>
              {asking === "not_for_us" ? "Set to not for us" : "Mark as done"}
            </AlertDialog.Title>
            <AlertDialog.Description>
              {asking ? bulkConfirmText(count, asking) : ""} Nothing is sent to the customer.
            </AlertDialog.Description>
            <div className="mt-4 flex justify-end gap-2">
              <AlertDialog.Close render={<Button variant="outline" tone="neutral" />}>
                Cancel
              </AlertDialog.Close>
              <Button
                tone="accent"
                onClick={() => {
                  const action = asking;
                  setAsking(null);
                  if (action) {
                    run(action);
                  }
                }}
              >
                {asking === "not_for_us" ? "Set to not for us" : "Mark as done"}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}
